document.addEventListener("DOMContentLoaded", () => {
  if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
    return;
  }

  gsap.registerPlugin(ScrollTrigger);

  // Prevent URL bar hide/show from triggering ScrollTrigger refresh and jumping the page on mobile
  ScrollTrigger.config({ ignoreMobileResize: true });

  // Instagram WebView Layout Jump Fix
  // Instagram's browser physically resizes on scroll, causing 100vh to constantly recalculate and shake.
  // We lock the hero (and any full-height panels) to a hard pixel value exactly once on load.
  if (window.innerWidth <= 768) {
    function setVH() {
      let vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    }
    setVH();
    let vw = window.innerWidth;
    window.addEventListener('resize', () => {
      if (window.innerWidth !== vw) {
        vw = window.innerWidth;
        setVH();
      }
    });
  }

  // Check if we are in the Shopify Theme Editor design mode
  const isDesignMode = typeof Shopify !== 'undefined' && Shopify.designMode;

  // 1. Initialize Lenis Smooth Scroll
  // Neutralize any native browser smooth-scroll that would fight Lenis
  document.documentElement.style.scrollBehavior = 'auto';

  let lenis;
  // Initialize Lenis only on desktop to prevent scroll fighting/jitter on mobile touch devices
  if (!isDesignMode && typeof window.Lenis !== 'undefined' && window.innerWidth > 768) {
    lenis = new window.Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)), // High-end snappy response
      smoothWheel: true,
      wheelMultiplier: 1.2,
      touchMultiplier: 2,
      infinite: false,
    });

    lenis.on('scroll', ScrollTrigger.update);

    gsap.ticker.add((time) => {
      lenis.raf(time * 1000);
    });

    gsap.ticker.lagSmoothing(0);

    window.rflxnsLenis = lenis;
  }

  // 2. Custom splitText Utility
  const splitTextUtility = (element) => {
    if (!element) return;
    const words = element.innerText.split(' ');
    element.innerHTML = '';
    words.forEach(word => {
      const wordContainer = document.createElement('span');
      wordContainer.classList.add('rflxns-word-container');
      wordContainer.style.display = 'inline-block';
      wordContainer.style.overflow = 'hidden';
      wordContainer.style.verticalAlign = 'top';

      const wordSpan = document.createElement('span');
      wordSpan.classList.add('rflxns-word');
      wordSpan.style.display = 'inline-block';
      wordSpan.style.transform = 'translateY(110%)'; // Hidden initially
      wordSpan.style.willChange = 'transform';
      wordSpan.innerHTML = word + '&nbsp;';

      wordContainer.appendChild(wordSpan);
      element.appendChild(wordContainer);
    });
  };


  // Ensure elements are immediately visible in Theme Editor mode
  if (isDesignMode) {
    gsap.set('.reveal', { opacity: 1, y: 0, clipPath: 'inset(0% 0% 0% 0%)' });
    gsap.set('.mock-card', { opacity: 1, y: 0, clipPath: 'inset(0% 0% 0% 0%)' });
    return;
  }

  // 3. Global Reveal Logic (kept separate from the app shell and homepage interaction modules)
  const revealElements = document.querySelectorAll('.reveal:not(.mock-card):not(.story-image-col)');
  revealElements.forEach((el) => {
    gsap.to(el, {
      scrollTrigger: {
        trigger: el,
        start: 'top 90%',
        toggleActions: 'play none none reverse'
      },
      opacity: 1,
      y: 0,
      duration: 0.8,
      ease: 'power3.out'
    });
  });

  // 4. Homepage Cinematic Animations
  const mmCinematic = gsap.matchMedia();
  mmCinematic.add("(min-width: 769px)", () => {

  // A. Hero Section & Logo Animation
  const heroCta = document.getElementById('hero-cta');
  const hero = document.getElementById('hero');
  const heroImage = hero ? hero.querySelector('.hero-media-image') : null;
  const navLogo = document.getElementById('nav-logo') || document.querySelector('.nav-logo');
  
  if (navLogo && hero) { // Only animate logo if the hero section exists (homepage)
    const isMobile = window.innerWidth <= 768;
  
  // Instagram WebView Layout Jump Fix
  // Instagram's browser physically resizes on scroll, causing 100vh to constantly recalculate.
  // We lock the hero to a hard pixel value exactly once on load so it never resizes.
  if (isMobile) {
    const heroPanel = document.getElementById('hero');
    if (heroPanel) {
      heroPanel.style.height = `${window.innerHeight}px`;
      heroPanel.style.minHeight = `${window.innerHeight}px`;
    }
  }
    const initialScale = isMobile ? 1.6 : 2.5;
    const initialY = window.innerHeight * (isMobile ? 0.32 : 0.38);

    // Initial state: centered and large
    gsap.set(navLogo, { 
      scale: initialScale, 
      y: initialY,
      transformOrigin: 'center center'
    });

    // 1. Reflections logo moves straight to header in first 30% of scroll
    gsap.to(navLogo, {
      scale: 1,
      y: 0,
      ease: 'power2.out',
      scrollTrigger: {
        trigger: 'body',
        start: 'top top',
        end: () => `+=${window.innerHeight * 0.3}`,
        scrub: 0.2
      }
    });

    // 2. Header stays docked permanently and never hides on scroll down
    const mainNav = document.getElementById('main-nav');
    if (mainNav) {
      mainNav.classList.remove('nav-hidden');
    }

    // 3. Scroll cue — dismiss once user passes 40% of the hero height
    const scrollCue = document.getElementById('hero-scroll-cue');
    if (scrollCue && hero) {
      ScrollTrigger.create({
        trigger: hero,
        start: 'top top',
        end: '40% top',
        onLeave: () => scrollCue.classList.add('is-hidden'),
        onEnterBack: () => scrollCue.classList.remove('is-hidden'),
      });

      // Also allow clicking the cue to smoothly scroll past the hero
      scrollCue.addEventListener('click', () => {
        const nextSection = hero.closest('.hero-scroll-container')?.nextElementSibling;
        if (nextSection) {
          nextSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else {
          window.scrollBy({ top: window.innerHeight, behavior: 'smooth' });
        }
      });
    }
  }
  if (heroCta) {
    gsap.fromTo(heroCta, 
      { y: 50, opacity: 0 }, 
      {
        y: 0,
        opacity: 1,
        duration: 0.8,
        ease: 'power3.out',
        delay: 2.2, // Waits for the intro-loader to partially finish
        scrollTrigger: {
          trigger: '#hero',
          start: 'top center',
        }
      }
    );
  }

    if (hero && heroImage) {
      gsap.fromTo(heroImage,
        {
          scale: 1.01,
          filter: 'brightness(0.82) saturate(0.88) contrast(1.04)'
        },
        {
          scale: 1.075,
          filter: 'brightness(1.02) saturate(1.16) contrast(1.12)',
          ease: 'none',
          scrollTrigger: {
            trigger: hero,
            start: 'top top',
            end: 'bottom top',
            scrub: 0.35
          }
        }
      );
    }

    // A2. Chapter image scroll color reveal
    const chapterSection = document.getElementById('chapter-teaser');
    const chapterImage = chapterSection ? chapterSection.querySelector('.chapter-teaser__image') : null;
    if (chapterSection && chapterImage) {
      gsap.fromTo(chapterImage,
        {
          y: 28,
          scale: 0.955,
          opacity: 0.84,
          filter: 'grayscale(1) sepia(0) saturate(0.26) contrast(1.14) brightness(0.44) drop-shadow(0 24px 48px rgba(0, 0, 0, 0.46))'
        },
        {
          y: 0,
          scale: 1.04,
          opacity: 1,
          filter: 'grayscale(0) sepia(0.02) saturate(1.06) contrast(1.04) brightness(0.9) drop-shadow(0 34px 72px rgba(0, 0, 0, 0.34))',
          ease: 'none',
          scrollTrigger: {
            trigger: chapterSection,
            start: 'top 76%',
            end: 'top 54%',
            scrub: 0.22
          }
        }
      );
    }

  // B. Split Categories
  const categoriesSection = document.getElementById('categories');
  const paneLeft = document.querySelector('.pane-left .pane-content');
  const paneRight = document.querySelector('.pane-right .pane-content');
  
  if (categoriesSection && paneLeft && paneRight) {
    const tlCategories = gsap.timeline({
      scrollTrigger: {
        trigger: categoriesSection,
        start: 'top 70%',
      }
    });

    tlCategories.fromTo([paneLeft, paneRight],
      { y: 60, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.8, stagger: 0.1, ease: 'power3.out' }
    );
  }

  // C. Drop 001 Rail — Section header + card stagger reveal
  const dropSection = document.getElementById('mock-products');
  const dropHeader = dropSection ? dropSection.querySelector('.rflxns-drop-header') : null;
  const dropCards = dropSection ? gsap.utils.toArray('.rflxns-drop-card') : [];

  if (dropSection && dropCards.length > 0) {
    const tlDrop = gsap.timeline({
      scrollTrigger: {
        trigger: dropSection,
        start: 'top 82%',
        once: true,
      }
    });

    if (dropHeader) {
      tlDrop.fromTo(dropHeader,
        { y: 28, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.6, ease: 'power3.out' },
        0
      );
    }

    tlDrop.fromTo(dropCards,
      { clipPath: 'inset(100% 0% 0% 0%)', opacity: 0 },
      {
        clipPath: 'inset(0% 0% 0% 0%)',
        opacity: 1,
        duration: 0.65,
        stagger: 0.07,
        ease: 'power3.out',
        clearProps: 'willChange'
      },
      dropHeader ? 0.14 : 0
    );
  }

  // D. Founder Story
  const storyOverlay = document.getElementById('story-overlay');
  if (storyOverlay) {
    const title = storyOverlay.querySelector('.story-title');
    if (title) splitTextUtility(title);

    const tlStory = gsap.timeline({
      scrollTrigger: {
        trigger: storyOverlay,
        start: 'top 60%',
      }
    });

    // Image slides up from its own mask seamlessly
    const storyImgCol = storyOverlay.querySelector('.story-image-col');
    const storyImg = storyOverlay.querySelector('.story-image-col img');
    
    if (storyImgCol && storyImg) {
      gsap.set(storyImgCol, { clipPath: 'inset(100% 0% 0% 0%)' });
      tlStory.to(storyImgCol, {
        clipPath: 'inset(0% 0% 0% 0%)',
        duration: 0.8,
        ease: 'power3.out'
      }, 0);
      
      tlStory.fromTo(storyImg,
        { scale: 1.15, y: 30 },
        { scale: 1, y: 0, duration: 0.8, ease: 'power3.out' },
        0
      );
    }

    // Split text reveal
    const words = storyOverlay.querySelectorAll('.rflxns-word');
    if (words.length > 0) {
      tlStory.to(words, {
        y: '0%',
        duration: 0.6,
        stagger: 0.05,
        ease: 'power3.out'
      }, 0.2);
    }

    // Paragraphs
    const storyParagraphs = storyOverlay.querySelectorAll('.story-body p');
    if (storyParagraphs.length > 0) {
      tlStory.fromTo(storyParagraphs,
        { y: 30, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.6, stagger: 0.1, ease: 'power3.out' },
        0.4
      );
    }
    
    // Signature
    const signature = storyOverlay.querySelector('.story-signature');
    if (signature) {
      tlStory.fromTo(signature,
        { opacity: 0, x: -20 },
        { opacity: 1, x: 0, duration: 0.6, ease: 'power2.out' },
        0.7
      );
    }
  }

  }); // End cinematic matchMedia

  // 6. PDP Experience Animations (Desktop stacked media zoom + parallax reveal)
  const pdpStages = gsap.utils.toArray('[data-pdp-media-stage]');
  if (pdpStages.length > 0) {
    const mmPdp = gsap.matchMedia();
    mmPdp.add("(min-width: 769px)", () => {
      pdpStages.forEach((stage) => {
        const img = stage.querySelector('.rflxns-pdp__media-image');
        if (!img) return;

        // A. Clip-path wipe reveal as stage scrolls into viewport
        gsap.fromTo(stage, {
          clipPath: 'inset(100% 0% 0% 0%)'
        }, {
          clipPath: 'inset(0% 0% 0% 0%)',
          duration: 1.2,
          ease: 'power4.out',
          scrollTrigger: {
            trigger: stage,
            start: 'top 88%',
            once: true
          }
        });

        // B. Dynamic parallax scroll translation
        mmCinematic.add("(min-width: 769px)", () => {
          gsap.fromTo(img, {
            yPercent: -10
          }, {
            yPercent: 10,
            ease: 'none',
            scrollTrigger: {
              trigger: stage,
              start: 'top bottom',
              end: 'bottom top',
              scrub: 0.1
            }
          });
        });
      });
    });
  }

  // 5. Final Refresh + In-Viewport Safety Net
  // Increased to 300ms to ensure Lenis and snap are fully initialized before refresh.
  // Safety net: any mock-card already visible in the viewport when the page loads
  // (e.g. if user refreshes mid-scroll) gets force-revealed so it's never left hidden.
  setTimeout(() => {
    ScrollTrigger.refresh();
    // Safety net: drop cards already visible at load time (mid-scroll refresh) get force-revealed
    gsap.utils.toArray('.rflxns-drop-card').forEach(card => {
      const rect = card.getBoundingClientRect();
      if (rect.top < window.innerHeight * 0.92) {
        gsap.set(card, { clipPath: 'inset(0% 0% 0% 0%)', opacity: 1 });
      }
    });
  }, 300);
});
